import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { STORAGE_KEY } from '../../util/storage-keys'
import { classMap } from 'lit/directives/class-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { formatCurrency } from '../../util/format'
import '../../util/tooltip'
import './dental-compare'
import { ApplicationService } from '../../services/application.service'
import { features as allFeatures } from '../../util/plans'
import { withBase } from '../../util/base-path'
import { getCostBreakdownDescription } from '../../util/cost-breakdown'
import { clearSelections } from '../../util/clear-selections'
import { orderDentalPlans } from '../../util/sort-plans'
import { getPlanYear, getEffectiveDateFromContext } from '../../util/pdf-config'
import {
  isReadOnlyFromUrl,
  shouldDisablePlanAction,
} from '../../util/read-only-actions'

interface PlanCard {
  planId: string
  planName: string
  monthlyPremium: string
  planType: 'dental' | 'vision'
  features: string[] | { display: string; value: any }[]
  applicantRate: any[]
  producerRecommended?: boolean
  rate?: any // For cost breakdown tooltip
  planRate?: any // For cost breakdown tooltip
  extraOver65?: string
}

@customElement('ancillary-dental-results')
export class AncillaryDentalResults extends LitElement {
  static styles = css`
    .container {
      border-radius: 4px;
      border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
      box-shadow: 0px 8px 16px 0px rgba(0, 76, 108, 0.08);
    }
    .border {
      border: 3px solid var(--dark-primary-dark, #006894);
    }
    .icon {
      color: white;
    }
    .plan-cards {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }
    .desktop-wrapper {
      box-sizing: border-box;
      padding: 24px;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 26px;
    }
    .mobile-wrapper {
      box-sizing: border-box;
      padding: 24px;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .details {
      display: flex;
      flex-direction: column;
      gap: 24px;
      flex: 3;
    }
    .price {
      flex: 1;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;
      background: var(--lh-color-primary-50, #ebf9ff);
      gap: 22px;
      padding: 24px;
    }

    .coverage-wrapper {
      display: flex;
      flex-direction: row;
      gap: 12px;
      flex-wrap: wrap;
    }
    .mobile-coverage-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      flex-wrap: wrap;
      row-gap: 8px;
    }
    .coverage {
      display: flex;
      flex-direction: row;
      gap: 4px;
    }
    .copay-wrapper {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .mobile-copay-wrapper {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    .mobile-price-wrapper {
      display: flex;
    }
    .copay {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .costs {
      display: flex;
      flex-direction: column;
      gap: 6px;
      align-items: center;
    }
    .price-buttons {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;
    }
    .price-text {
      white-space: pre-wrap;
    }
    .hide {
      display: none;
    }
    .top-banner {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }
  `

  @consume({ context: shopperContext, subscribe: true })
  @state()
  shopperContextValue?: ShopperContextType

  @consume({ context: breakpointContext, subscribe: true })
  @state()
  breakpoint!: BREAKPOINT

  @state()
  isShopper: boolean = true

  @state()
  loading: boolean = false

  @state()
  selectedPanel: 'list' | 'compare' = 'list'

  private switchToListTab = () => {
    const tab = this.renderRoot?.querySelector(
      'lh-tab[panel="list"]'
    ) as HTMLElement | null
    if (tab) {
      tab.click()
    } else {
      const group = this.renderRoot?.querySelector(
        'lh-tab-group'
      ) as HTMLElement | null
      if (group) {
        group.setAttribute('default-panel', 'list')
      }
    }
  }

  private baseUrl: string = ''
  private brochureLink: string = ''

  private syncLoadingFromContext() {
    const hasData = !!this.shopperContextValue?.quoteData
    this.loading = !hasData
    if (hasData) {
      this.selectedPanel = 'list'
    }
  }

  private handleQuoteUpdated = () => {
    this.loading = false
    this.selectedPanel = 'list'
  }

  private handleEditQuote() {
    // Return to previous step using browser history
    window.history.back()
  }

  private handleSelectPlan(planId: string) {
    this.apply(planId)
  }

  private topBanner(plan: PlanCard) {
    if (!plan.producerRecommended) return null
    let label
    if (plan.producerRecommended) {
      label = !this.shopperContextValue?.isAgent
        ? 'Your agent recommended this plan.'
        : 'Recommended plan'
    } else {
      label = 'You originally applied for this plan.'
    }
    return html`<lh-container
      theme="primary-dark"
      spacing="none"
      content-width="fluid"
      padding-top="sm"
      padding-bottom="sm"
      gutters="sm"
      ><div class="top-banner">
        <span class="icon">
          <lh-icon name="seal-check"></lh-icon>
        </span>
        <lh-typography>${label}</lh-typography>
      </div></lh-container
    >`
  }

  private handleRecommendedPlan(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const plans = quote?.crossSalePlans || quote?.jsonPlans || ([] as any[])
    const idx = plans.findIndex((p: any) => p.planId === planId)
    if (idx < 0) return
    plans.forEach((p: any) => (p.producerRecommended = false))
    const selected = plans[idx]
    ;(selected as any).producerRecommended = true
    plans.splice(idx, 1)
    plans.unshift(selected)
    this.requestUpdate()

    // Ask parent to switch to List View tab (no route change)
    this.dispatchEvent(
      new CustomEvent('switch-to-list', { bubbles: true, composed: true })
    )

    // Broadcast so other components re-render and highlight
    document.dispatchEvent(
      new CustomEvent('quote-data-updated', {
        detail: { quoteData: this.shopperContextValue?.quoteData },
      })
    )

    // Store dental plan selection for email
    try {
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_ID, planId)
    } catch {
      // non-blocking
    }

    // If vision plans are available, navigate to vision results so user can recommend there too
    const hasVisionPlans =
      (this.shopperContextValue?.quoteData?.individualPlans || []).length > 0
    if (hasVisionPlans) {
      this.navigateToVision()
    } else {
      // Dental-only scenario - check if delivery method is email
      const isEmail =
        this.shopperContextValue?.formData?.deliveryMethod === 'e' ||
        (this.shopperContextValue?.quoteData?.quoteApplication
          ?.bolApplication &&
          (
            this.shopperContextValue.quoteData.quoteApplication
              .bolApplication as any
          )['deliveryMethod'] === 'e')

      if (isEmail) {
        // Mark vision as not selected for dental-only email
        try {
          sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'false')
        } catch {
          // non-blocking
        }
        // Stay on the page - user will click "Send Selected Plans" button to navigate
        return
      }
      this.doApplyDentalOnly(planId)
    }
  }

  connectedCallback() {
    super.connectedCallback()
    if (typeof window !== 'undefined') {
      if (
        window.location.host.includes('shopperstage.bcbsnc.com') ||
        window.location.host.includes('localhost')
      ) {
        this.baseUrl = 'https://assetsps.bcbsnc.com/assets/email'
      } else if (window.location.host.includes('shopper.bcbsnc.com')) {
        this.baseUrl = 'https://assets.bcbsnc.com/assets/email'
      } else {
        this.baseUrl = ''
      }
    }
    this.updateBrochureLink()

    // Initialize loading based on current context and listen for quote updates
    this.syncLoadingFromContext()
    document.addEventListener('quote-data-updated', this.handleQuoteUpdated)
  }

  private updateBrochureLink() {
    this.brochureLink =
      getPlanYear(getEffectiveDateFromContext(this.shopperContextValue)) ===
      '2027'
        ? withBase('/images/U35521_2027_Dental_Vision_Combo_Brochure_508.pdf')
        : this.baseUrl +
          '/2026/U35521_Dental_Vision_combo_brochure_for_individual_Final_508.pdf'
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('quote-data-updated', this.handleQuoteUpdated)
  }

  firstUpdated(): void {
    // Double-check loading state after initial render
    this.syncLoadingFromContext()
  }

  updated(changedProps: Map<string | number | symbol, unknown>): void {
    super.updated(changedProps as any)
    if (changedProps.has('shopperContextValue')) {
      this.syncLoadingFromContext()
      this.updateBrochureLink()
    }
  }

  private apply(planId: string) {
    // Update plan in result
    this.updatePlan(planId)
    // Persist dental selection and navigate to vision page for final submission
    try {
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_ID, planId)
    } catch {
      // non-blocking
    }

    // Check for immediate-binder flow with vision=false (dental-only)
    const isImmediateBinderFlow =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const visionDisabled =
      new URLSearchParams(window.location.search).get('vision') === 'false'
    if (isImmediateBinderFlow && visionDisabled) {
      this.submitImmediateBinderForm(planId)
      return
    }

    // If vision plans exist, navigate to vision; otherwise submit dental-only
    const hasVisionPlans =
      (this.shopperContextValue?.quoteData?.individualPlans || []).length > 0
    if (hasVisionPlans) {
      this.navigateToVision()
    } else {
      this.doApplyDentalOnly(planId)
    }
  }

  private updatePlan(planId: string) {
    const plans =
      this.shopperContextValue?.quoteData?.crossSalePlans ||
      this.shopperContextValue?.quoteData?.jsonPlans ||
      []
    for (const plan of plans) {
      if (plan.planId === planId) {
        // Update applicant rates if needed
        if (
          plan.applicantRate &&
          this.shopperContextValue?.quoteData?.quoteApplication?.applicants
        ) {
          plan.applicantRate.forEach((rate: any, index: number) => {
            if (
              this.shopperContextValue?.quoteData?.quoteApplication.applicants[
                index
              ]
            ) {
              this.shopperContextValue.quoteData.quoteApplication.applicants[
                index
              ].individualRate = rate.monthlyPremium
            }
          })
        }
        break
      }
    }
  }

  private getPlanMonthlyRate(plan: any): string {
    const rawRate = Array.isArray(plan?.planRate)
      ? plan?.planRate?.[0]?.monthlyPremium
      : plan?.planRate?.monthlyPremium

    if (rawRate === undefined || rawRate === null || rawRate === '') {
      return ''
    }

    return String(rawRate)
  }

  private submitImmediateBinderForm(dentalPlanId: string): boolean {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
      (p: any) => p.id === dentalPlanId || p.planId === dentalPlanId
    )

    if (!dentalPlan) {
      console.error('Dental plan not found for immediate-binder:', dentalPlanId)
      return false
    }

    const visionPlanId = sessionStorage.getItem(STORAGE_KEY.VISION_PLAN_ID)
    const visionSelected =
      sessionStorage.getItem(STORAGE_KEY.VISION_PLAN_SELECTED) === 'true' &&
      !!visionPlanId
    const visionPlan = visionSelected
      ? (quote?.individualPlans || []).find(
          (p: any) => p.planId === visionPlanId || p.id === visionPlanId
        )
      : null

    const form = document.createElement('form')
    form.method = 'post'
    form.action = `${import.meta.env.VITE_BCBSNC_URL}/sapps/buyonline/application/IBinderPayment_ancillaryApplicants.action`
    form.style.display = 'none'

    const appendHiddenInput = (name: string, id: string, value: string) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.id = id
      input.value = value
      form.appendChild(input)
    }

    appendHiddenInput('immediateBinderPayment.isDental', 'isDental', 'true')
    appendHiddenInput(
      'immediateBinderPayment.isVision',
      'isVision',
      visionPlan ? 'true' : 'false'
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planName',
      'dentalPlan',
      String(dentalPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planName',
      'visionPlan',
      String(visionPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planRate',
      'dentalRate',
      this.getPlanMonthlyRate(dentalPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planRate',
      'visionRate',
      this.getPlanMonthlyRate(visionPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planID',
      'dentalPlanID',
      String(dentalPlan?.id || dentalPlan?.planId || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planID',
      'visionPlanID',
      String(visionPlan?.id || visionPlan?.planId || '')
    )

    document.body.appendChild(form)
    form.submit()
    return true
  }

  private doApplyDentalOnly(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
      (p: any) => p.planId === planId
    )
    if (!dentalPlan) {
      console.error('Dental plan not found:', planId)
      return
    }

    const shoppingCart = [
      {
        planId: dentalPlan.planId,
        plan: dentalPlan,
      },
    ]

    ApplicationService.submitApplication(
      quote,
      shoppingCart,
      true, // skipVision when submitting dental-only
      false, // do not skip dental
      '',
      this.shopperContextValue?.producerSession || null
    )
    // Clear dental selection after successful dental-only submission.
    clearSelections({ dental: true })
  }

  private handleSelectedPlans() {
    // Check if vision was selected - if not, mark it as skipped for dental-only email flow
    try {
      const visionSelected = sessionStorage.getItem(
        STORAGE_KEY.VISION_PLAN_SELECTED
      )
      if (visionSelected !== 'true') {
        sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'false')
      }
    } catch {
      // non-blocking
    }

    // Navigate to Producer Email Entry page
    window.history.pushState(
      {},
      '',
      withBase('producer-email-entry') +
        window.location.search +
        window.location.hash
    )
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  // Removed immediate submission from dental selection; submission occurs from vision page.

  private navigateToVision() {
    const targetUrl =
      withBase('vision-result') + window.location.search + window.location.hash
    window.history.pushState({}, '', targetUrl)

    // Note: popstate events do NOT fire when calling pushState()!
    // They only fire on browser back/forward navigation.
    // We must manually trigger a re-render or dispatch a custom event.

    // Dispatch custom events that the container will listen for
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { path: targetUrl },
        bubbles: true,
        composed: true,
      })
    )
    document.dispatchEvent(
      new CustomEvent('route-change', {
        bubbles: true,
      })
    )
  }

  private handleSkipToVision() {
    // Mark dental as skipped (not selected) instead of clearing
    // This allows vision page to detect that dental was explicitly skipped
    try {
      sessionStorage.setItem(STORAGE_KEY.DENTAL_PLAN_SELECTED, 'false')
      sessionStorage.removeItem(STORAGE_KEY.DENTAL_PLAN_ID)
    } catch {
      // non-blocking
    }
    this.navigateToVision()
  }

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  private get isReadOnlyMode() {
    return isReadOnlyFromUrl()
  }

  private get hasDentalPlans() {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlans = quote?.crossSalePlans || quote?.jsonPlans || []
    return dentalPlans.length > 0
  }

  private get hasVisionPlans() {
    const visionPlans =
      this.shopperContextValue?.quoteData?.individualPlans || []
    return visionPlans.length > 0
  }

  private get disableDentalActionButton() {
    return shouldDisablePlanAction({
      readOnly: this.isReadOnlyMode,
      product: 'dental',
      hasDentalPlans: this.hasDentalPlans,
      hasVisionPlans: this.hasVisionPlans,
    })
  }

  // cost breakdown moved to shared util

  private getActionButton(plan: PlanCard, size: 'md' | 'lg') {
    const isEmail =
      this.shopperContextValue?.formData?.deliveryMethod === 'e' ||
      (this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication &&
        (
          this.shopperContextValue.quoteData.quoteApplication
            .bolApplication as any
        )['deliveryMethod'] === 'e')
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const analyticsIdSuffix = isImmediateBinder ? ':immediate-binder' : ''
    const shouldDisableAction = this.disableDentalActionButton
    return isEmail
      ? html`<lh-button
          variant="filled"
          size=${size}
          full-width
          icon="seal-check"
          icon-position="left"
          ?disabled=${shouldDisableAction}
          analytics-id="ancillary-rate-quote:dental-results:${plan.producerRecommended
            ? 'keep-this-plan'
            : 'recommend-plan'}${analyticsIdSuffix}:button-text"
          @click=${() => this.handleRecommendedPlan(plan.planId)}
        >
          ${plan.producerRecommended ? 'Keep this plan' : 'Recommend Plan'}
        </lh-button>`
      : html`<lh-button
          variant="filled"
          size=${size}
          icon-position="left"
          full-width
          icon="note-pencil"
          ?disabled=${shouldDisableAction}
          analytics-id="ancillary-rate-quote:dental-results:${plan.producerRecommended
            ? 'keep-this-plan'
            : 'choose-plan'}${analyticsIdSuffix}:button-text"
          @click=${() => this.handleSelectPlan(plan.planId)}
        >
          ${plan.producerRecommended ? 'Keep this plan' : 'Choose Plan'}
        </lh-button>`
  }

  private renderPlanCard(plan: PlanCard) {
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    return html`
      <div
        class=${classMap({
          container: true,
          border: !!plan.producerRecommended,
        })}
      >
        ${this.topBanner(plan)}
        <div
          slot="content"
          class=${classMap({
            'desktop-wrapper': !this.isMobile,
            'mobile-wrapper': this.isMobile,
          })}
        >
          <div class="details">
            <lh-image
              src="${new URL('../../images/dental_blue.png', import.meta.url)
                .href}"
              alt="${plan.planName}"
            ></lh-image>
            <div>
              <lh-typography size="lg" weight="700">
                ${plan.planName}
              </lh-typography>
            </div>
            ${this.isMobile
              ? html`<div>
                  ${isImmediateBinder
                    ? html`<lh-typography type="heading" alignment="left"
                        >${formatCurrency(plan.monthlyPremium)}</lh-typography
                      >`
                    : html`<rq-tooltip
                        .description=${getCostBreakdownDescription(
                          plan,
                          this.shopperContextValue?.quoteData,
                          !!this.shopperContextValue?.isAgent
                        )}
                        position="top"
                      >
                        <lh-typography type="heading" alignment="left"
                          >${formatCurrency(plan.monthlyPremium)}</lh-typography
                        ></rq-tooltip
                      >`}
                  ${plan.extraOver65 && isImmediateBinder
                    ? html`<lh-typography size="text-sm" alignment="left"
                        >${unsafeHTML(
                          plan.extraOver65.replace(
                            /(\$[\d,]+(?:\.\d+)?)/g,
                            '<strong>$1</strong>'
                          )
                        )}</lh-typography
                      >`
                    : ''}
                </div>`
              : ``}
            ${plan.features &&
            Array.isArray(plan.features) &&
            plan.features.length > 0
              ? html`<div class="copay-wrapper">
                  ${plan.features.map(
                    (feature: any) => html`
                      <div class="copay">
                        <lh-typography size="text-lg" alignment="left">
                          <strong class="price-text"
                            >${typeof feature === 'string'
                              ? feature
                              : unsafeHTML(feature.display)}</strong
                          ><br />
                          <span class="price-text"
                            >${typeof feature === 'string'
                              ? ''
                              : unsafeHTML(feature.value)}</span
                          >
                        </lh-typography>
                      </div>
                    `
                  )}
                </div>`
              : ``}
            <div
              class=${classMap({
                'coverage-wrapper': !this.isMobile,
                'mobile-coverage-wrapper': this.isMobile,
              })}
            >
              <lh-typography variant="label" size="label-sm"
                >Check coverage in this plan</lh-typography
              >
              <div class="coverage">
                ${(() => {
                  const planYear = getPlanYear(
                    getEffectiveDateFromContext(this.shopperContextValue)
                  )
                  const productOn = plan.planType
                  let href = ''
                  const baseUrl = this.baseUrl || ''
                  if (productOn === 'dental') {
                    if (plan.planId === 'IDTL-PL1') {
                      href =
                        planYear === '2027'
                          ? withBase(
                              '/images/U44014a_2027_Dental_Blue_Core_1000_Brochure_508.pdf'
                            )
                          : baseUrl +
                            '/2026/U44014a_Dental_Blue_Core_1000_Plan_Brochure_508.pdf'
                    } else if (plan.planId === 'IDTL-PL2') {
                      href =
                        planYear === '2027'
                          ? withBase(
                              '/images/U44014b_2027_Dental_Blue_Preventive_Plus_brochure_508.pdf'
                            )
                          : baseUrl +
                            '/2026/U44014b_Dental_Blue_Preventive_Plus_Plan_Brochure_508.pdf'
                    } else if (plan.planId === 'IDTL-PL3') {
                      href =
                        planYear === '2027'
                          ? withBase(
                              '/images/U44014c_2027_Dental_Blue_Value_1500_brochure_508.pdf'
                            )
                          : baseUrl +
                            '/2026/U44014c_Dental_Blue_Value_1500_PPO_Plan_Brochure_508.pdf'
                    }
                  }
                  return href
                    ? html`<lh-button-group size="sm"
                        ><lh-button
                          target="_blank"
                          variant="text"
                          size="sm"
                          href="${href}"
                          analytics-id="ancillary-rate-quote:dental-results:summary-of-benefits-and-coverage-link:button-text"
                          >Summary of Benefits and Coverage (PDF)</lh-button
                        ><lh-button
                          target="_blank"
                          variant="text"
                          size="sm"
                          analytics-id="ancillary-rate-quote:dental-results:product-brochure-link:button-text"
                          href="${this.brochureLink}"
                        >
                          Product Brochure (PDF)
                        </lh-button></lh-button-group
                      >`
                    : null
                })()}
              </div>
            </div>
          </div>
          ${this.isMobile
            ? html` <div class="price-buttons">
                  ${this.getActionButton(plan, 'lg')}
                </div>
              </div>`
            : ``}
          ${!this.isMobile
            ? html`<div class="price">
                <div class="costs">
                  <lh-typography size="text-sm" alignment="center"
                    >Cost per month</lh-typography
                  >
                  <div class=${classMap({ 'full-width': this.isMobile })}>
                    ${isImmediateBinder
                      ? html`<lh-typography type="heading" alignment="center"
                          >${formatCurrency(plan.monthlyPremium)}</lh-typography
                        >`
                      : html`<rq-tooltip
                          .description=${getCostBreakdownDescription(
                            plan,
                            this.shopperContextValue?.quoteData,
                            !!this.shopperContextValue?.isAgent
                          )}
                          position="left"
                        >
                          <lh-typography type="heading" alignment="center"
                            >${formatCurrency(
                              plan.monthlyPremium
                            )}</lh-typography
                          ></rq-tooltip
                        >`}
                  </div>
                </div>
                <div
                  style="display: flex; flex-direction: column; justify-content: center; align-items: flex-start;"
                >
                  ${plan.extraOver65 && isImmediateBinder
                    ? html`<lh-typography size="text-sm" alignment="center"
                        >${unsafeHTML(
                          plan.extraOver65.replace(
                            /(\$[\d,]+(?:\.\d+)?)/g,
                            '<strong>$1</strong>'
                          )
                        )}</lh-typography
                      >`
                    : ''}
                </div>
                <div class="price-buttons">
                  ${this.getActionButton(plan, 'md')}
                </div>
              </div>`
            : ''}
        </div>
      </div>
    `
  }

  render() {
    // Show skip button if cross-sale vision plans are available
    const showSkipButton =
      this.shopperContextValue?.quoteData?.individualPlans &&
      this.shopperContextValue?.quoteData.individualPlans.length > 0
    const isAgent = !!this.shopperContextValue?.isAgent
    const referralSource =
      this.shopperContextValue?.quoteData?.quoteApplication?.bolApplication
        ?.referralSource
    const isProducerEntered = referralSource === 'PRODUCER_ENTERED'
    const isImmediateBinderFlow =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const canSkip =
      (isAgent ||
        isProducerEntered ||
        new URLSearchParams(window.location.search).get('quoteId')) &&
      !!showSkipButton &&
      !isImmediateBinderFlow

    // Hide Back button if quote or quoteId param is present
    const urlParams = new URLSearchParams(window.location.search)
    const hasQuoteParams = urlParams.get('quote') || urlParams.get('quoteId')

    return html`
      <lh-container gutters="none" spacing="lg" context-id="dental-results">
        <lh-grid>
          <lh-grid-item>
            <lh-typography type="heading" size=${this.isMobile ? 'xl' : 'lg'}
              >Dental plan selection</lh-typography
            >
          </lh-grid-item>
        </lh-grid>
        <lh-tab-group default-panel=${this.selectedPanel}>
          <lh-tab slot="nav" panel="list">List View</lh-tab>
          <lh-tab slot="nav" panel="compare">Compare View</lh-tab>

          <lh-tab-panel name="list">
            ${this.loading
              ? html`<lh-loader></lh-loader>`
              : html`
                  <lh-container gutters="none" margin-bottom="md">
                    <lh-typography
                      >Use the
                      <a
                        href="https://healthnav.bcbsnc.com/?ci=COMMERCIAL&amp;network_id=15"
                        target="_blank"
                        >Find a Dentist tool<lh-icon
                          size="md"
                          name="arrow-square-out"
                        ></lh-icon
                      ></a>
                      to search the provider network before choosing a plan.
                    </lh-typography>
                    <lh-typography>
                      <strong>Note:</strong> Pricing may differ for individuals
                      65 and older.
                    </lh-typography>
                    ${this.shopperContextValue?.formData?.deliveryMethod ===
                      'e' ||
                    (this.shopperContextValue?.quoteData?.quoteApplication
                      ?.bolApplication &&
                      (
                        this.shopperContextValue.quoteData.quoteApplication
                          .bolApplication as any
                      )['deliveryMethod'] === 'e')
                      ? html`<lh-button
                          variant="outlined"
                          icon-position="left"
                          icon="paper-plane-tilt"
                          analytics-id="ancillary-rate-quote:dental-results:email-delivery:send-selected-plans-to-my-client:button-text"
                          @click=${this.handleSelectedPlans}
                          >Send Selected Plans to My Client</lh-button
                        >`
                      : ''}
                  </lh-container>
                  <div class="plan-cards">
                    ${(() => {
                      const plans =
                        this.shopperContextValue?.quoteData?.crossSalePlans ||
                        this.shopperContextValue?.quoteData?.jsonPlans ||
                        []
                      if (Array.isArray(plans) && plans.length > 0) {
                        const ordered = orderDentalPlans(plans)
                        return ordered.map((plan: any) => {
                          // Parse jsonDetails to get feature values
                          let details: Record<string, any> = {}
                          if (plan.details?.jsonDetails) {
                            try {
                              details = JSON.parse(plan.details.jsonDetails)
                            } catch (e) {
                              console.error('Failed to parse jsonDetails:', e)
                            }
                          }

                          // Filter features from plans.ts where list='y', planCode='IDTL', and planName matches planId
                          const planFeatures = allFeatures
                            .filter(
                              (f) =>
                                f.list === 'y' &&
                                f.planCode === 'IDTL' &&
                                f.planName === plan.planId
                            )
                            .map((f) => {
                              let value =
                                details[f.key] || f.defaultValue || 'N/A'

                              // Append additional service information if available
                              if (
                                details['PREVENTIVE_SERVICES'] &&
                                f.display === 'Preventive'
                              ) {
                                value =
                                  value +
                                  '<br/>' +
                                  details['PREVENTIVE_SERVICES']
                              } else if (
                                details['BASIC_SERVICES'] &&
                                f.display === 'Basic'
                              ) {
                                value =
                                  value + '<br/>' + details['BASIC_SERVICES']
                              } else if (
                                details['MAJOR_SERVICES'] &&
                                f.display === 'Major'
                              ) {
                                value =
                                  value + '<br/>' + details['MAJOR_SERVICES']
                              }

                              return {
                                display: f.display,
                                value: value,
                              }
                            })

                          const dentalCard: PlanCard = {
                            planId: plan.planId || '',
                            planName: plan.description || '',
                            monthlyPremium:
                              typeof plan.rate?.monthlyPremium === 'number'
                                ? plan.rate.monthlyPremium.toFixed(2)
                                : String(plan.rate?.monthlyPremium || ''),
                            planType: 'dental',
                            features: planFeatures,
                            applicantRate: plan.applicantRate || [],
                            producerRecommended: !!plan.producerRecommended,
                            rate: plan.rate, // Include rate object for cost breakdown tooltip
                            extraOver65:
                              (details.EXTRAOVER65 as string | undefined) ??
                              undefined,
                          }

                          return this.renderPlanCard(dentalCard)
                        })
                      }
                      return html`
                        <lh-typography>
                          No dental plans available
                        </lh-typography>
                      `
                    })()}
                  </div>
                `}
          </lh-tab-panel>
          <lh-tab-panel name="compare">
            <lh-container gutters="none">
              <rq-dental-compare
                @switch-to-list=${this.switchToListTab}
              ></rq-dental-compare>
            </lh-container>
          </lh-tab-panel>
        </lh-tab-group>
        <lh-button-group
          alignment="${this.isMobile
            ? 'center'
            : hasQuoteParams
              ? 'right'
              : 'auto'}"
          orientation="${this.isMobile ? 'vertical' : 'horizontal'}"
        >
          ${!hasQuoteParams
            ? html`<lh-button
                variant="text"
                icon="caret-left"
                icon-position="left"
                @click=${this.handleEditQuote}
                ?full-width=${this.isMobile}
                analytics-id="ancillary-rate-quote:dental-results:back-to-quote:button-text"
              >
                Back
              </lh-button>`
            : ''}
          ${canSkip
            ? html`<lh-button
                @click=${this.handleSkipToVision}
                ?full-width=${this.isMobile}
                analytics-id="ancillary-rate-quote:dental-results:skip-to-vision:button-text"
                >Skip Dental</lh-button
              >`
            : ''}
        </lh-button-group>
      </lh-container>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ancillary-dental-results': AncillaryDentalResults
  }
}
